import { createRouter, createWebHistory } from 'vue-router'
import { saveReturnTo } from '@/auth/session'
import AppLayout from '@/components/layout/AppLayout.vue'
import { resolveChatContext } from '@/runtime/chatContext'
import { isEmbeddedWindow } from '@/utils/windowContext'
import type { ChatSurface, LaunchContext } from '@/config'

function getRouteMetaValue<T>(name: string, defaultValue: T, matched: ReadonlyArray<{ meta: Record<string, unknown> }>): T {
  for (let index = matched.length - 1; index >= 0; index -= 1) {
    const value = matched[index]?.meta?.[name]
    if (value !== undefined) {
      return value as T
    }
  }
  return defaultValue
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/AuthCallbackView.vue'),
    },
    {
      path: '/auth/mcp/start',
      name: 'mcp-auth-start',
      component: () => import('@/views/McpOAuthStartView.vue'),
    },
    {
      path: '/auth/mcp/callback',
      name: 'mcp-auth-callback',
      component: () => import('@/views/McpOAuthCallbackView.vue'),
    },
    {
      path: '/embed/:botName',
      component: AppLayout,
      meta: { authGate: true, authGateScope: 'view', authSurface: 'embedded' },
      children: [
        {
          path: '',
          name: 'embed-chat',
          component: () => import('@/views/EmbedChatView.vue'),
          meta: { authSurface: 'embedded' },
        },
      ],
    },
    {
      path: '/assignments/:botName',
      name: 'assignment-chat',
      component: () => import('@/views/AssignmentChatView.vue'),
      meta: { authGate: true, authSurface: 'embedded', chatSurface: 'canvas_assignment', launchContext: 'lti_assignment' },
    },
    {
      path: '/canvas',
      component: AppLayout,
      meta: { authGate: true, authSurface: 'embedded', chatSurface: 'canvas_workspace', launchContext: 'lti_study' },
      children: [
        {
          path: '',
          name: 'canvas-landing',
          component: () => import('@/views/CanvasLandingView.vue'),
          meta: { chatSurface: 'canvas_workspace', launchContext: 'lti_study' },
        },
        {
          path: 'bots/:botName',
          name: 'canvas-bot-name',
          component: () => import('@/views/CanvasChatView.vue'),
          meta: { chatSurface: 'canvas_bot', launchContext: 'lti_study' },
        },
        {
          path: 'bots/:botId/:botSlug?/sessions/:sessionId?',
          name: 'canvas-bot-session',
          component: () => import('@/views/CanvasChatView.vue'),
          meta: { chatSurface: 'canvas_bot', launchContext: 'lti_study' },
        },
      ],
    },
    {
      path: '/',
      component: AppLayout,
      meta: { authGate: true, authSurface: 'full', chatSurface: 'desktop_workspace', launchContext: 'cas' },
      children: [
        {
          path: '',
          name: 'landing',
          component: () => import('@/views/LandingView.vue'),
        },
        {
          path: 'bots/:botName',
          name: 'bot-name',
          component: () => import('@/views/ChatView.vue'),
        },
        {
          path: 'bots/:botId/:botSlug?/sessions/:sessionId?',
          name: 'bot-session',
          component: () => import('@/views/ChatView.vue'),
        },
      ],
    },
  ],
})

router.beforeEach((to) => {
  if (to.name === 'login' || to.name === 'auth-callback' || to.name === 'mcp-auth-start' || to.name === 'mcp-auth-callback') {
    return true
  }

  if (!to.matched.some(record => record.meta.authGate)) {
    return true
  }

  const botToken = typeof to.params.botName === 'string'
    ? to.params.botName
    : typeof to.params.botId === 'string'
      ? to.params.botId
      : null
  const authSurface = getRouteMetaValue<'full' | 'embedded'>('authSurface', 'full', to.matched)
  const surface = getRouteMetaValue<ChatSurface>('chatSurface', authSurface === 'embedded' ? 'helper_embed' : 'desktop_workspace', to.matched)
  const launchContext = getRouteMetaValue<LaunchContext>('launchContext', 'cas', to.matched)

  const context = resolveChatContext({ botId: botToken, surface, launchContext })
  if (context.access !== 'requires_auth') {
    return true
  }

  const isIframeHostedDesktopBot = authSurface === 'full'
    && to.path.startsWith('/bots/')
    && isEmbeddedWindow()
  if (authSurface === 'embedded' || isIframeHostedDesktopBot) {
    return true
  }

  saveReturnTo(to.fullPath)
  return {
    name: 'login',
    query: { reason: 'auth_required' },
  }
})

export default router
